import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Spin } from "antd";
import { observer } from "mobx-react";
import { Suspense, lazy, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Route, RouterProvider, createHashRouter, createRoutesFromElements } from "react-router-dom";
import { Notices, useI18nProvider, useUpsellProvider } from "@devowl-wp/react-cookie-banner-admin";
import { JOB_DONE_EVENT_PREFIX } from "@devowl-wp/real-queue";
import { SuspenseChunkTranslation } from "@devowl-wp/utils";
import { BlockerLayout } from "./config/blocker/layout.js";
import { ConfigFooter } from "./config/footer.js";
import { ConfigHeader } from "./config/header.js";
import { NoticeAnonymousScriptNotWritable } from "./config/noticeAnonymousScriptNotWritable.js";
import { NoticeBannerActiveNoCookies } from "./config/noticeBannerActiveNoCookies.js";
import { NoticeBannerlessConsent } from "./config/noticeBannerlessConsent.js";
import { NoticeNoManager } from "./config/noticeNoManager.js";
import { NoticeRevisionNeedsRetrigger } from "./config/noticeRevisionNeedsRetrigger.js";
import { NoticeServiceDataProcessingInUnsafeCountries } from "./config/noticeServiceDataProcessingInUnsafeCountries.js";
import { RouterScrollToTop } from "./routerScrollToTop.js";
import { useScannerProgress } from "../hooks/useScannerProgress.js";
import { useStores } from "../store/stores.js";
import { AUTOMATIC_SCAN_STATER_QUEUE_JOB } from "../types/queue.js";
import { __, _i, _n, _x } from "../utils/i18n.js";
import { ConsentDbConsentMigrationV2Badge } from "./config/consent/dbConsentMigrationV2Badge.js";
const CSS_SPINNER_IN_CONTENT = {
    display: "block",
    marginTop: 10
};
// Lazy load components
const createSuspenseComponent = (tab, children)=>({
        Component: ()=>/*#__PURE__*/ _jsx(SuspenseChunkTranslation, {
                chunkFile: __webpack_get_script_filename__(`chunk-config-tab-${tab}`),
                options: ()=>useStores().optionStore,
                fallback: /*#__PURE__*/ _jsx(Spin, {
                    spinning: true,
                    style: CSS_SPINNER_IN_CONTENT
                }),
                children: children
            })
    });
const DashboardCards = ()=>import(/* webpackChunkName: "chunk-config-tab-dashboard" */ "./config/dashboard/cards.js").then(({ DashboardCards })=>createSuspenseComponent("dashboard", /*#__PURE__*/ _jsx(DashboardCards, {})));
const SettingsForm = ()=>import(/* webpackChunkName: "chunk-config-tab-settings" */ "./config/settings/form.js").then(({ SettingsForm })=>createSuspenseComponent("settings", /*#__PURE__*/ _jsx(SettingsForm, {})));
const ScannerList = ()=>import(/* webpackChunkName: "chunk-config-tab-scanner" */ "./config/scanner/list.js").then(({ ScannerList })=>createSuspenseComponent("scanner", /*#__PURE__*/ _jsx(ScannerList, {})));
const CookieGroupsTabLayout = ()=>import(/* webpackChunkName: "chunk-config-tab-cookies" */ "./config/cookies/groups/layout.js").then(({ CookieGroupsTabLayout })=>createSuspenseComponent("cookies", /*#__PURE__*/ _jsx(CookieGroupsTabLayout, {})));
const CookiesList = ()=>import(/* webpackChunkName: "chunk-config-tab-cookies" */ "./config/cookies/list.js").then(({ CookiesList })=>createSuspenseComponent("cookies", /*#__PURE__*/ _jsx(CookiesList, {})));
const CookieTemplateCenter = ()=>import(/* webpackChunkName: "chunk-config-tab-cookies" */ "./config/cookies/templateCenter.js").then(({ CookieTemplateCenter })=>createSuspenseComponent("cookies", /*#__PURE__*/ _jsx(CookieTemplateCenter, {})));
const CookieEditForm = ()=>import(/* webpackChunkName: "chunk-config-tab-cookies" */ "./config/cookies/form.js").then(({ CookieEditForm })=>createSuspenseComponent("cookies", /*#__PURE__*/ _jsx(CookieEditForm, {})));
const TcfLayout = ()=>import(/* webpackChunkName: "chunk-config-tab-tcf" */ "./config/cookies/tcf/layout.js").then(({ TcfLayout })=>createSuspenseComponent("tcf", /*#__PURE__*/ _jsx(TcfLayout, {})));
const TcfVendorConfigurationList = ()=>import(/* webpackChunkName: "chunk-config-tab-tcf" */ "./config/cookies/tcf/list.js").then(({ TcfVendorConfigurationList })=>createSuspenseComponent("tcf", /*#__PURE__*/ _jsx(TcfVendorConfigurationList, {})));
const TcfVendorSelector = ()=>import(/* webpackChunkName: "chunk-config-tab-tcf" */ "./config/cookies/tcf/vendorSelector.js").then(({ TcfVendorSelector })=>createSuspenseComponent("tcf", /*#__PURE__*/ _jsx(TcfVendorSelector, {})));
const TcfVendorConfigurationForm = ()=>import(/* webpackChunkName: "chunk-config-tab-tcf" */ "./config/cookies/tcf/form.js").then(({ TcfVendorConfigurationForm })=>createSuspenseComponent("tcf", /*#__PURE__*/ _jsx(TcfVendorConfigurationForm, {})));
const ConsentTabRouter = ()=>import(/* webpackChunkName: "chunk-config-tab-consent" */ "./config/consent/tabRouter.js").then(({ ConsentTabRouter })=>createSuspenseComponent("consent", /*#__PURE__*/ _jsx(ConsentTabRouter, {})));
const BlockerList = ()=>import(/* webpackChunkName: "chunk-config-tab-blocker" */ "./config/blocker/list.js").then(({ BlockerList })=>createSuspenseComponent("blocker", /*#__PURE__*/ _jsx(BlockerList, {})));
const BlockerTemplateCenter = ()=>import(/* webpackChunkName: "chunk-config-tab-blocker" */ "./config/blocker/templateCenter.js").then(({ BlockerTemplateCenter })=>createSuspenseComponent("blocker", /*#__PURE__*/ _jsx(BlockerTemplateCenter, {})));
const BlockerEditForm = ()=>import(/* webpackChunkName: "chunk-config-tab-blocker" */ "./config/blocker/form.js").then(({ BlockerEditForm })=>createSuspenseComponent("blocker", /*#__PURE__*/ _jsx(BlockerEditForm, {})));
const ImportExportCards = ()=>import(/* webpackChunkName: "chunk-config-tab-import" */ "./config/import/cards.js").then(({ ImportExportCards })=>createSuspenseComponent("import", /*#__PURE__*/ _jsx(ImportExportCards, {})));
const ConfigLicensing = ()=>import(/* webpackChunkName: "chunk-config-tab-licensing" */ "./config/licensing/licensing.js").then(({ ConfigLicensing })=>createSuspenseComponent("licensing", /*#__PURE__*/ _jsx(ConfigLicensing, {})));
const ConfigLicensingReactLazy = /*#__PURE__*/ lazy(()=>ConfigLicensing().then(({ Component })=>({
            default: Component
        })));
const ConfigApp = ()=>{
    const router = useMemo(()=>createHashRouter(createRoutesFromElements(/*#__PURE__*/ _jsxs(Route, {
            element: /*#__PURE__*/ _jsx(ConfigAppLayout, {}),
            children: [
                /*#__PURE__*/ _jsx(Route, {
                    index: true,
                    lazy: DashboardCards
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: "settings/:tab?",
                    lazy: SettingsForm
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: "scanner",
                    lazy: ScannerList
                }),
                /*#__PURE__*/ _jsxs(Route, {
                    path: "cookies",
                    lazy: CookieGroupsTabLayout,
                    children: [
                        /*#__PURE__*/ _jsxs(Route, {
                            path: "tcf-vendors",
                            lazy: TcfLayout,
                            children: [
                                /*#__PURE__*/ _jsx(Route, {
                                    index: true,
                                    lazy: TcfVendorConfigurationList
                                }),
                                /*#__PURE__*/ _jsx(Route, {
                                    path: "new",
                                    lazy: TcfVendorSelector
                                }),
                                /*#__PURE__*/ _jsx(Route, {
                                    path: "edit/:vendorConfiguration",
                                    lazy: TcfVendorConfigurationForm
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: ":cookieGroup?",
                            lazy: CookiesList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: ":cookieGroup/new",
                            lazy: CookieTemplateCenter
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: ":cookieGroup/edit/:cookie",
                            lazy: CookieEditForm
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: "consent/:tab?",
                    lazy: ConsentTabRouter
                }),
                /*#__PURE__*/ _jsxs(Route, {
                    path: "blocker",
                    element: /*#__PURE__*/ _jsx(BlockerLayout, {}),
                    children: [
                        /*#__PURE__*/ _jsx(Route, {
                            index: true,
                            lazy: BlockerList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "new",
                            lazy: BlockerTemplateCenter
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "edit/:blocker",
                            lazy: BlockerEditForm
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: "import",
                    lazy: ImportExportCards
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: "licensing",
                    lazy: ConfigLicensing
                })
            ]
        }))), []);
    return /*#__PURE__*/ _jsx(RouterProvider, {
        router: router
    });
};
const ConfigAppLayout = observer(()=>{
    const { percent: scanPercent, remaining: scanRemaining, currentJob } = useScannerProgress();
    const { optionStore, checklistStore } = useStores();
    const { others: { isPro, isLicensed, proUrl, hints, showLicenseFormImmediate, colorScheme: [, , badgeColor], isDemoEnv, capabilities: { activate_plugins } }, fomoCoupon, checkSavingConsentViaRestApiEndpointWorkingHtml, templateUpdateNoticeHtml, templateSuccessorsNoticeHtml, googleConsentModeNoticesHtml, servicesWithEmptyPrivacyPolicyNoticeHtml } = optionStore;
    const [isReady, setIsReady] = useState(false);
    // Initially fetch settings here so the form is shown
    useLayoutEffect(()=>{
        optionStore.fetchSettings().then(()=>{
            setIsReady(true);
        });
        // Listen to the automatic scan starter and update the checklist
        const listener = ()=>{
            checklistStore.fetchChecklist();
        };
        const event = `${JOB_DONE_EVENT_PREFIX}${AUTOMATIC_SCAN_STATER_QUEUE_JOB}`;
        document.addEventListener(event, listener);
        return ()=>{
            document.removeEventListener(event, listener);
        };
    }, []);
    const [I18nContextProvider, i18nContextValue] = useI18nProvider({
        __,
        _i,
        _n,
        _x
    });
    const [UpsellContextProvider, upsellContextValue] = useUpsellProvider({
        fomoCoupon,
        isPro,
        isLicensed,
        isDemoEnv,
        proUrl,
        hint: hints?.proDialog
    }, {}, {
        inherit: [
            "isLicensed",
            "fomoCoupon"
        ]
    });
    const navLinkClassName = useCallback(({ isActive })=>isActive ? "nav-tab nav-tab-active" : "nav-tab", []);
    return /*#__PURE__*/ _jsx(I18nContextProvider, {
        value: i18nContextValue,
        children: /*#__PURE__*/ _jsx(UpsellContextProvider, {
            value: upsellContextValue,
            children: showLicenseFormImmediate ? /*#__PURE__*/ _jsx(Suspense, {
                fallback: /*#__PURE__*/ _jsx(Spin, {
                    spinning: true,
                    style: CSS_SPINNER_IN_CONTENT
                }),
                children: /*#__PURE__*/ _jsx(ConfigLicensingReactLazy, {})
            }) : /*#__PURE__*/ _jsxs(Spin, {
                spinning: !isReady,
                children: [
                    /*#__PURE__*/ _jsx(ConfigHeader, {}),
                    /*#__PURE__*/ _jsxs("nav", {
                        className: "nav-tab-wrapper wp-clearfix",
                        children: [
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/",
                                className: navLinkClassName,
                                end: true,
                                children: __("Dashboard")
                            }),
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/settings",
                                className: navLinkClassName,
                                children: __("Settings")
                            }),
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/scanner",
                                className: navLinkClassName,
                                children: scanRemaining > 0 && scanPercent > 0 && currentJob?.group_position > 0 ? /*#__PURE__*/ _jsx(Badge, {
                                    count: `${scanPercent} %`,
                                    style: {
                                        top: -11,
                                        fontSize: 10,
                                        height: 16,
                                        lineHeight: "16px",
                                        background: badgeColor
                                    },
                                    children: __("Scanner")
                                }) : __("Scanner")
                            }),
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/cookies",
                                className: navLinkClassName,
                                children: __("Services (Cookies)")
                            }),
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/blocker",
                                className: navLinkClassName,
                                children: __("Content Blocker")
                            }),
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/consent",
                                className: navLinkClassName,
                                children: /*#__PURE__*/ _jsx(ConsentDbConsentMigrationV2Badge, {
                                    children: __("Consent")
                                })
                            }),
                            /*#__PURE__*/ _jsx(NavLink, {
                                to: "/import",
                                className: navLinkClassName,
                                children: __("Import / Export")
                            }),
                            window.location.host !== "try.devowl.io" && activate_plugins && /*#__PURE__*/ _jsx(NavLink, {
                                to: "/licensing",
                                className: navLinkClassName,
                                children: __("Licensing")
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                href: __("https://devowl.io/support/"),
                                className: "nav-tab",
                                target: "_blank",
                                rel: "noreferrer",
                                children: __("Support")
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(NoticeRevisionNeedsRetrigger, {}),
                    /*#__PURE__*/ _jsx(NoticeServiceDataProcessingInUnsafeCountries, {}),
                    /*#__PURE__*/ _jsx(Notices, {
                        notices: [
                            {
                                severity: "error",
                                message: checkSavingConsentViaRestApiEndpointWorkingHtml
                            },
                            {
                                severity: "warning",
                                message: servicesWithEmptyPrivacyPolicyNoticeHtml
                            },
                            {
                                severity: "warning",
                                message: templateUpdateNoticeHtml
                            },
                            {
                                severity: "warning",
                                message: templateSuccessorsNoticeHtml
                            },
                            ...googleConsentModeNoticesHtml.map((s)=>({
                                    severity: "warning",
                                    message: s
                                }))
                        ]
                    }),
                    /*#__PURE__*/ _jsx(NoticeBannerActiveNoCookies, {}),
                    /*#__PURE__*/ _jsx(NoticeAnonymousScriptNotWritable, {}),
                    /*#__PURE__*/ _jsx(NoticeNoManager, {}),
                    /*#__PURE__*/ _jsx(NoticeBannerlessConsent, {}),
                    /*#__PURE__*/ _jsx(RouterScrollToTop, {}),
                    /*#__PURE__*/ _jsx(Suspense, {
                        fallback: /*#__PURE__*/ _jsx(Spin, {
                            spinning: true,
                            style: CSS_SPINNER_IN_CONTENT
                        }),
                        children: isReady && /*#__PURE__*/ _jsx(Outlet, {})
                    }),
                    /*#__PURE__*/ _jsx(ConfigFooter, {})
                ]
            })
        })
    });
});
export { ConfigApp, ConfigAppLayout, createSuspenseComponent, CSS_SPINNER_IN_CONTENT };
